import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { motion } from 'framer-motion';

const LandingPageAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState({
    pageViews: 0,
    uniqueVisitors: 0,
    avgScrollDepth: 0,
    ctaClicks: 0,
    conversionRate: 0,
    topEvents: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate fetching analytics data
    // In production, this would fetch from Google Analytics API
    const timer = setTimeout(() => {
      setAnalyticsData({
        pageViews: 0,
        uniqueVisitors: 0,
        avgScrollDepth: 0,
        ctaClicks: 0,
        conversionRate: 0,
        topEvents: [
          { name: 'CTA Clicks', count: 0, percentage: 0 },
          { name: 'Demo Clicks', count: 0, percentage: 0 },
          { name: 'Social Icon Clicks', count: 0, percentage: 0 },
          { name: 'Feature Card Views', count: 0, percentage: 0 },
          { name: 'Scroll to 50%', count: 0, percentage: 0 }
        ]
      });
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const metricCards = [
    {
      title: 'Page Views',
      value: analyticsData?.pageViews?.toLocaleString(),
      icon: 'Eye',
      color: 'var(--color-primary)',
      description: 'Total landing page visits'
    },
    {
      title: 'Unique Visitors',
      value: analyticsData?.uniqueVisitors?.toLocaleString(),
      icon: 'Users',
      color: 'var(--color-accent)',
      description: 'Distinct visitors'
    },
    {
      title: 'Avg Scroll Depth',
      value: `${analyticsData?.avgScrollDepth}%`,
      icon: 'ArrowDown',
      color: 'var(--color-success)',
      description: 'Average page scroll'
    },
    {
      title: 'CTA Clicks',
      value: analyticsData?.ctaClicks?.toLocaleString(),
      icon: 'MousePointer',
      color: 'var(--color-warning)',
      description: 'Call-to-action engagements'
    },
    {
      title: 'Conversion Rate',
      value: `${analyticsData?.conversionRate?.toFixed(1)}%`,
      icon: 'TrendingUp',
      color: 'var(--color-primary)',
      description: 'Visitors to sign-ups'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Landing Page Analytics</h1>
              <p className="text-muted-foreground">Track visitor behavior, conversion funnels, and engagement metrics</p>
            </div>
            <Button
              variant="outline"
              onClick={() => window.open('https://analytics.google.com', '_blank')}
            >
              <Icon name="ExternalLink" size={16} className="mr-2" />
              View in GA4
            </Button>
          </div>
        </motion.div>

        {/* Setup Instructions */}
        <motion.div 
          className="bg-gradient-to-r from-zinc-500/10 to-zinc-500/10 border-2 border-zinc-500/30 rounded-xl p-6 mb-8"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-zinc-500/20 flex items-center justify-center">
              <Icon name="Info" size={24} color="var(--color-primary)" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground mb-2">Google Analytics Setup Required</h3>
              <p className="text-sm text-muted-foreground mb-4">
                To start tracking landing page analytics, add your Google Analytics Measurement ID to the environment variables.
              </p>
              <div className="bg-card rounded-lg p-4 border border-border">
                <p className="text-xs font-mono text-muted-foreground mb-2">In your .env file:</p>
                <code className="text-sm font-mono text-foreground bg-muted px-3 py-2 rounded block">
                  VITE_GA_MEASUREMENT_ID=G-XXXXXXXXXX
                </code>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                <span className="text-sm text-muted-foreground">Tracking events are already implemented and ready to collect data</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Metrics Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {metricCards?.map((metric, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-card rounded-xl p-6 border border-border shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${metric?.color}20` }}>
                  <Icon name={metric?.icon} size={24} color={metric?.color} />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-1">{metric?.value}</h3>
              <p className="text-sm font-medium text-muted-foreground mb-1">{metric?.title}</p>
              <p className="text-xs text-muted-foreground">{metric?.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Tracked Events */}
        <motion.div 
          className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {/* Top Events */}
          <div className="bg-card rounded-xl p-6 border border-border shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground">Top Events</h2>
              <Icon name="Activity" size={20} color="var(--color-primary)" />
            </div>
            <div className="space-y-4">
              {analyticsData?.topEvents?.map((event, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-foreground">{event?.name}</span>
                      <span className="text-sm text-muted-foreground">{event?.count}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary rounded-full h-2 transition-all duration-500"
                        style={{ width: `${event?.percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Conversion Funnel */}
          <div className="bg-card rounded-xl p-6 border border-border shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground">Conversion Funnel</h2>
              <Icon name="Filter" size={20} color="var(--color-accent)" />
            </div>
            <div className="space-y-4">
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">Landing Page View</span>
                  <span className="text-sm text-muted-foreground">100%</span>
                </div>
                <div className="w-full bg-primary/20 rounded-lg h-12 flex items-center justify-center">
                  <span className="text-sm font-semibold text-foreground">0 visitors</span>
                </div>
              </div>
              <div className="flex justify-center">
                <Icon name="ArrowDown" size={20} color="var(--color-muted-foreground)" />
              </div>
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">CTA Click</span>
                  <span className="text-sm text-muted-foreground">0%</span>
                </div>
                <div className="w-full bg-accent/20 rounded-lg h-12 flex items-center justify-center" style={{ width: '0%' }}>
                  <span className="text-sm font-semibold text-foreground">0 clicks</span>
                </div>
              </div>
              <div className="flex justify-center">
                <Icon name="ArrowDown" size={20} color="var(--color-muted-foreground)" />
              </div>
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">Sign Up</span>
                  <span className="text-sm text-muted-foreground">0%</span>
                </div>
                <div className="w-full bg-success/20 rounded-lg h-12 flex items-center justify-center" style={{ width: '0%' }}>
                  <span className="text-sm font-semibold text-foreground">0 signups</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tracked Events Reference */}
        <motion.div 
          className="bg-card rounded-xl p-6 border border-border shadow-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Code" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Tracked Events</h2>
              <p className="text-sm text-muted-foreground">All events currently being tracked on the landing page</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: 'page_view', description: 'Landing page visits' },
              { name: 'scroll_depth', description: 'User scroll behavior (25%, 50%, 75%, 100%)' },
              { name: 'cta_click', description: 'Call-to-action button clicks' },
              { name: 'demo_click', description: 'Demo account button clicks' },
              { name: 'social_icon_click', description: 'Social media icon interactions' },
              { name: 'feature_card_click', description: 'Feature card engagements' },
              { name: 'workflow_step_click', description: 'Workflow step navigation' },
              { name: 'platform_icon_click', description: 'Platform icon clicks' },
              { name: 'auth_form_submit', description: 'Authentication form submissions' },
              { name: 'google_signin_click', description: 'Google sign-in attempts' },
              { name: 'auth_toggle_click', description: 'Sign up/Sign in toggle' },
              { name: 'conversion', description: 'User signup/login conversions' },
              { name: 'funnel_step', description: 'Conversion funnel progression' }
            ]?.map((event, index) => (
              <div key={index} className="bg-muted/50 rounded-lg p-4 border border-border">
                <div className="flex items-start gap-2 mb-2">
                  <Icon name="Zap" size={16} color="var(--color-accent)" className="mt-0.5" />
                  <code className="text-sm font-mono text-foreground font-semibold">{event?.name}</code>
                </div>
                <p className="text-xs text-muted-foreground">{event?.description}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LandingPageAnalytics;
